import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { buildAppMeta, SITE_URL } from "@/lib/seo";
import { useMemo, useState } from "react";
import { RequireAuth } from "@/components/RequireAuth";
import { AppShell } from "@/components/AppShell";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Download, MoreHorizontal, Plus, Receipt } from "lucide-react";
import { useUnbilledSessions } from "@/hooks/useUnbilledSessions";
import { toast } from "sonner";
import { fmtMoney, type InvoiceStatus } from "@/lib/invoicing";

type ClientLite = { id: string; student_name: string };
type LineItemLite = { session_id: string | null };
type Invoice = {
  id: string;
  invoice_number: string;
  status: InvoiceStatus;
  issue_date: string;
  due_date: string | null;
  total_cents: number;
  amount_paid_cents: number;
  currency: string;
  coach_student_id: string;
  invoice_line_items?: LineItemLite[] | null;
};

type DisplayStatus = InvoiceStatus | "overdue";

const STATUS_PILL: Record<DisplayStatus, string> = {
  paid: "bg-[#e6f4ea] text-[#054d28]",
  sent: "bg-[#e8eefb] text-[#1d4ed8]",
  partial: "bg-amber-100 text-amber-800",
  overdue: "bg-[#fdebec] text-[#a72027]",
  void: "bg-muted text-muted-foreground",
  draft: "border border-border text-muted-foreground",
};

const STATUS_LABEL: Record<DisplayStatus, string> = {
  paid: "Paid",
  sent: "Sent",
  partial: "Partial",
  overdue: "Overdue",
  void: "Void",
  draft: "Draft",
};

const AVATAR_PALETTE = [
  "bg-[#9fe870] text-[#054d28]",
  "bg-[#7dd3fc] text-[#075985]",
  "bg-[#fcd34d] text-[#78350f]",
  "bg-[#f0abfc] text-[#701a75]",
  "bg-[#fda4af] text-[#881337]",
  "bg-[#a5b4fc] text-[#312e81]",
];

function initials(name: string) {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "?";
}

function paletteFor(seed: string) {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
}

function fmtDateShort(s: string | null | undefined) {
  if (!s) return "—";
  const [y, m, d] = s.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return s;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function isOverdue(inv: Invoice) {
  if (inv.status !== "sent" && inv.status !== "partial") return false;
  if (!inv.due_date) return false;
  return inv.due_date < todayIso();
}

function displayStatus(inv: Invoice): DisplayStatus {
  return isOverdue(inv) ? "overdue" : inv.status;
}

function sessionsCount(inv: Invoice) {
  const items = inv.invoice_line_items ?? [];
  const ids = new Set(items.map((i) => i.session_id).filter(Boolean));
  return ids.size || items.length;
}

export const Route = createFileRoute("/app/invoices")({
  head: () => ({
    meta: buildAppMeta({
      title: "Invoices — CoachQuick",
      description: "Create, send, and track invoices for your coaching clients with Stripe-powered payments.",
      path: "/app/invoices",
    }),
    links: [{ rel: "canonical", href: `${SITE_URL}/app/invoices` }],
  }),
  component: () => (
    <RequireAuth role="coach">
      <InvoicesPage />
    </RequireAuth>
  ),
});

function InvoicesPage() {
  const { user } = useAuth();
  const userId = user?.id;
  const qc = useQueryClient();
  const nav = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const [createClientId, setCreateClientId] = useState("");
  const [busy, setBusy] = useState(false);
  const [busyClientId, setBusyClientId] = useState<string | null>(null);
  const unbilledQuery = useUnbilledSessions();
  const unbilled = unbilledQuery.data;

  const invoicesQuery = useQuery({
    queryKey: ["invoices", userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("invoices" as never)
        .select(
          "id, invoice_number, status, issue_date, due_date, total_cents, amount_paid_cents, currency, coach_student_id, invoice_line_items(session_id)",
        )
        .eq("coach_id", userId!)
        .order("issue_date", { ascending: false });
      if (error) throw error;
      return (data as unknown as Invoice[]) ?? [];
    },
  });

  const clientsQuery = useQuery({
    queryKey: ["clients-lite", userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data } = await supabase
        .from("coach_students")
        .select("id, student_name")
        .eq("coach_id", userId!)
        .order("student_name");
      return (data as ClientLite[]) ?? [];
    },
  });

  const invoices = invoicesQuery.data ?? [];
  const clients = clientsQuery.data ?? [];
  const clientName = useMemo(() => {
    const m = new Map(clients.map((c) => [c.id, c.student_name]));
    return (id: string) => m.get(id) ?? "Client";
  }, [clients]);

  const monthLabel = useMemo(
    () => new Date().toLocaleString(undefined, { month: "long", year: "numeric" }),
    [],
  );

  const monthInvoices = useMemo(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    return invoices.filter((i) => {
      const [iy, im] = i.issue_date.slice(0, 10).split("-").map(Number);
      return iy === y && im - 1 === m;
    });
  }, [invoices]);

  const stats = useMemo(() => {
    const outstandingInvoices = invoices.filter(
      (i) => i.status === "sent" || i.status === "partial",
    );
    const outstanding = outstandingInvoices.reduce(
      (s, i) => s + (i.total_cents - i.amount_paid_cents),
      0,
    );
    const overdueInvoices = invoices.filter(isOverdue);
    const overdue = overdueInvoices.reduce(
      (s, i) => s + (i.total_cents - i.amount_paid_cents),
      0,
    );
    const paidThisMonth = monthInvoices
      .filter((i) => i.status === "paid")
      .reduce((s, i) => s + i.amount_paid_cents, 0);
    const paidCount = monthInvoices.filter((i) => i.status === "paid").length;
    return {
      outstanding,
      outstandingCount: outstandingInvoices.length,
      paidThisMonth,
      paidCount,
      overdue,
      overdueInvoices,
    };
  }, [invoices, monthInvoices]);

  async function createInvoiceForClient(clientId: string): Promise<string | null> {
    if (!user || !clientId) return null;
    const { data: numData, error: numErr } = await supabase.rpc(
      "next_invoice_number" as never,
      { _coach_id: user.id } as never,
    );
    if (numErr) throw numErr;
    const invoice_number = numData as unknown as string;

    const { data: profile } = await supabase
      .from("coach_profile" as never)
      .select("default_payment_instructions")
      .eq("coach_id", user.id)
      .maybeSingle();

    const { data, error } = await supabase
      .from("invoices" as never)
      .insert({
        coach_id: user.id,
        coach_student_id: clientId,
        invoice_number,
        status: "draft",
        payment_instructions: (profile as any)?.default_payment_instructions ?? null,
      } as never)
      .select("id")
      .single();
    if (error) throw error;
    return (data as any).id as string;
  }

  async function createInvoice() {
    if (!user || !createClientId) return;
    setBusy(true);
    try {
      const id = await createInvoiceForClient(createClientId);
      if (!id) return;
      toast.success("Draft invoice created");
      setCreateOpen(false);
      setCreateClientId("");
      qc.invalidateQueries({ queryKey: ["invoices", userId] });
      nav({ to: "/app/invoices/$invoiceId", params: { invoiceId: id } });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create invoice");
    } finally {
      setBusy(false);
    }
  }

  async function createInvoiceFromUnbilled(clientId: string) {
    setBusyClientId(clientId);
    try {
      const id = await createInvoiceForClient(clientId);
      if (!id) return;
      toast.success("Draft invoice created — add the sessions to bill");
      qc.invalidateQueries({ queryKey: ["invoices", userId] });
      nav({ to: "/app/invoices/$invoiceId", params: { invoiceId: id } });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create invoice");
    } finally {
      setBusyClientId(null);
    }
  }

  function exportCsv() {
    const rows = [
      ["invoice_number", "client", "issued", "due", "sessions", "amount", "paid", "status"].join(
        ",",
      ),
      ...invoices.map((inv) =>
        [
          csv(inv.invoice_number),
          csv(clientName(inv.coach_student_id)),
          csv(inv.issue_date ?? ""),
          csv(inv.due_date ?? ""),
          String(sessionsCount(inv)),
          (inv.total_cents / 100).toFixed(2),
          (inv.amount_paid_cents / 100).toFixed(2),
          STATUS_LABEL[displayStatus(inv)],
        ].join(","),
      ),
    ].join("\n");
    const blob = new Blob([rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `invoices-${todayIso()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const overdueNumbers = stats.overdueInvoices.map((i) => i.invoice_number);
  const overdueSub =
    stats.overdueInvoices.length === 0
      ? "0 invoices"
      : `${stats.overdueInvoices.length} invoice${stats.overdueInvoices.length === 1 ? "" : "s"} · ${overdueNumbers.slice(0, 2).join(", ")}${overdueNumbers.length > 2 ? "…" : ""}`;

  return (
    <AppShell>
      <div className="-mx-4 -my-6 min-h-[calc(100vh-4rem)] bg-canvas-soft px-4 py-6 md:-mx-8 md:px-8">
        {/* Header */}
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              {monthLabel} · {monthInvoices.length} invoice{monthInvoices.length === 1 ? "" : "s"}
            </p>
            <h1 className="mt-2 font-display text-4xl font-black tracking-tight md:text-5xl">
              Invoices
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Issued, sent, paid. Bundle unbilled lessons into a single invoice in two clicks.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={exportCsv}
              disabled={invoices.length === 0}
              className="rounded-[10px]"
            >
              <Download className="h-4 w-4" /> Export
            </Button>
            <Button onClick={() => setCreateOpen(true)} className="rounded-[10px]">
              <Plus className="h-4 w-4" /> New invoice
            </Button>
          </div>
        </div>

        {/* Stat tiles */}
        <div className="grid gap-4 md:grid-cols-3">
          <StatTile
            label="Paid this month"
            value={invoicesQuery.isLoading ? null : fmtMoney(stats.paidThisMonth)}
            valueClass="text-[#054d28] dark:text-[#7ed492]"
            sub={`${stats.paidCount} invoice${stats.paidCount === 1 ? "" : "s"}`}
          />
          <StatTile
            label="Outstanding"
            value={invoicesQuery.isLoading ? null : fmtMoney(stats.outstanding)}
            sub={`${stats.outstandingCount} invoice${stats.outstandingCount === 1 ? "" : "s"}`}
          />
          <StatTile
            label="Overdue"
            value={invoicesQuery.isLoading ? null : fmtMoney(stats.overdue)}
            valueClass="text-[#a72027] dark:text-[#f87171]"
            tone="overdue"
            sub={overdueSub}
          />
        </div>

        {/* Unbilled lessons */}
        {unbilledQuery.isLoading ? (
          <div className="mt-6 overflow-hidden rounded-[16px] border bg-card p-4">
            <Skeleton className="h-12 w-full" />
          </div>
        ) : unbilled && unbilled.totalSessions > 0 ? (
          <section className="mt-6">
            <div className="mb-2 flex items-end justify-between gap-2">
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                Unbilled lessons · {unbilled.totalClients} client
                {unbilled.totalClients === 1 ? "" : "s"} · {unbilled.totalSessions} lesson
                {unbilled.totalSessions === 1 ? "" : "s"}
                {unbilled.estimatedTotalCents > 0
                  ? ` · ~${fmtMoney(unbilled.estimatedTotalCents)}`
                  : ""}
              </p>
            </div>
            <div className="overflow-hidden rounded-[16px] border bg-card">
              <ul className="divide-y">
                {unbilled.groups.map((g) => {
                  const dates = g.sessions
                    .map((s) => s.session_date)
                    .filter(Boolean)
                    .sort();
                  const earliest = dates[0];
                  const latest = dates[dates.length - 1];
                  const rangeLabel =
                    earliest && latest && earliest !== latest
                      ? `${fmtDateShort(earliest)} → ${fmtDateShort(latest)}`
                      : earliest
                        ? fmtDateShort(earliest)
                        : "";
                  const isBusy = busyClientId === g.clientId;
                  return (
                    <li
                      key={g.clientId}
                      className="flex items-center gap-3 px-5 py-3.5"
                    >
                      <Avatar className="h-7 w-7">
                        <AvatarFallback
                          className={`text-[10px] font-bold ${paletteFor(g.clientName)}`}
                        >
                          {initials(g.clientName)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">{g.clientName}</p>
                        <p className="text-xs text-muted-foreground">
                          {g.sessions.length} unbilled lesson
                          {g.sessions.length === 1 ? "" : "s"}
                          {rangeLabel ? ` · ${rangeLabel}` : ""}
                        </p>
                      </div>
                      {g.estimatedTotalCents > 0 && (
                        <div className="hidden text-right sm:block">
                          <p className="text-sm font-bold">
                            ~{fmtMoney(g.estimatedTotalCents)}
                          </p>
                          <p className="text-[11px] text-muted-foreground">
                            at {fmtMoney(g.defaultRateCents)}/lesson
                          </p>
                        </div>
                      )}
                      <Button
                        size="sm"
                        onClick={() => createInvoiceFromUnbilled(g.clientId)}
                        disabled={isBusy}
                        className="rounded-[8px]"
                      >
                        <Receipt className="h-3.5 w-3.5" />
                        {isBusy ? "Creating…" : "Create invoice"}
                      </Button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </section>
        ) : null}

        {/* Table */}

        <div className="mt-4 overflow-hidden rounded-[16px] border bg-card">
          {invoicesQuery.isLoading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : invoices.length === 0 ? (
            <div className="px-6 py-16 text-center">
              <p className="text-sm text-muted-foreground">
                No invoices yet. Create your first one to start tracking.
              </p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  <th className="px-5 py-3 text-left font-semibold">Invoice</th>
                  <th className="px-5 py-3 text-left font-semibold">Client</th>
                  <th className="px-5 py-3 text-left font-semibold">Issued</th>
                  <th className="px-5 py-3 text-left font-semibold">Due</th>
                  <th className="px-5 py-3 text-right font-semibold">Sessions</th>
                  <th className="px-5 py-3 text-right font-semibold">Amount</th>
                  <th className="px-5 py-3 text-left font-semibold">Status</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => {
                  const overdue = isOverdue(inv);
                  const ds = displayStatus(inv);
                  const name = clientName(inv.coach_student_id);
                  const goto = () =>
                    nav({ to: "/app/invoices/$invoiceId", params: { invoiceId: inv.id } });
                  return (
                    <tr
                      key={inv.id}
                      className="cursor-pointer border-b last:border-0 hover:bg-muted/40"
                      onClick={goto}
                    >
                      <td className="px-5 py-3.5 font-mono text-[13px] text-muted-foreground">
                        {inv.invoice_number}
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2.5">
                          <Avatar className="h-7 w-7">
                            <AvatarFallback
                              className={`text-[10px] font-bold ${paletteFor(name)}`}
                            >
                              {initials(name)}
                            </AvatarFallback>
                          </Avatar>
                          <span className="font-medium">{name}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5 text-muted-foreground">
                        {fmtDateShort(inv.issue_date)}
                      </td>
                      <td
                        className={`px-5 py-3.5 ${overdue ? "font-semibold text-[#a72027] dark:text-[#f87171]" : "text-muted-foreground"}`}
                      >
                        {fmtDateShort(inv.due_date)}
                      </td>
                      <td className="px-5 py-3.5 text-right text-muted-foreground">
                        {sessionsCount(inv)}
                      </td>
                      <td className="px-5 py-3.5 text-right font-bold">
                        {fmtMoney(inv.total_cents, inv.currency)}
                      </td>
                      <td className="px-5 py-3.5">
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${STATUS_PILL[ds]}`}
                        >
                          {STATUS_LABEL[ds]}
                        </span>
                      </td>
                      <td className="px-5 py-3.5" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-2">
                          {overdue && (
                            <Button
                              size="sm"
                              className="h-7 rounded-[8px] px-3 text-xs"
                              onClick={() =>
                                toast.success(`Reminder sent for ${inv.invoice_number}`)
                              }
                            >
                              Remind
                            </Button>
                          )}
                          <button
                            type="button"
                            onClick={goto}
                            className="flex h-7 w-7 items-center justify-center rounded-[8px] border text-muted-foreground hover:text-foreground"
                            aria-label="Invoice actions"
                          >
                            <MoreHorizontal className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New invoice</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Client</Label>
              <Select value={createClientId} onValueChange={setCreateClientId}>
                <SelectTrigger className="mt-1.5">
                  <SelectValue placeholder="Pick a client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.student_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button onClick={createInvoice} disabled={!createClientId || busy}>
              {busy ? "Creating…" : "Create draft"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function StatTile({
  label,
  value,
  valueClass,
  sub,
  tone,
}: {
  label: string;
  value: string | null;
  valueClass?: string;
  sub?: string;
  tone?: "overdue";
}) {
  const overdue = tone === "overdue";
  return (
    <Card
      className={`relative overflow-hidden rounded-[16px] border bg-card p-5 shadow-none ${
        overdue ? "border-[#a72027]/15" : ""
      }`}
    >
      {overdue && (
        <>
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 dark:hidden"
            style={{
              background:
                "linear-gradient(180deg, rgba(253,235,236,1) 0%, rgba(253,235,236,0) 75%)",
            }}
          />
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 hidden dark:block"
            style={{
              background:
                "linear-gradient(180deg, rgba(167,32,39,0.18) 0%, rgba(167,32,39,0) 75%)",
            }}
          />
        </>
      )}
      <div className="relative">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p
          className={`mt-2 font-display text-[40px] font-black leading-none tracking-tight ${valueClass ?? ""}`}
        >
          {value ?? <Skeleton className="inline-block h-9 w-28" />}
        </p>
        {sub && <p className="mt-3 text-xs text-muted-foreground">{sub}</p>}
      </div>
    </Card>
  );
}

function csv(s: string) {
  if (s == null) return "";
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}
